// Preparing a file for upload as an attachment.

/**
 * The name to upload a file under. Composed Unicode (NFC): macOS/iOS hand out names with decomposed
 * accents ("a" + combining "¨" for "ä"), which look the same but are different characters, and which
 * the server could not put into a download header. Control characters and path separators are dropped.
 */
export function uploadFileName(name: string | null | undefined): string {
  const cleaned = String(name ?? '')
    .normalize('NFC')
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[\\/]/g, '_')
    .trim();
  return cleaned || 'file';
}

/**
 * The file's contents read into memory, as a new File with a clean name. On iPhone/iPad a photo picked
 * from Photos (or pasted) can be a lazily converted file that Safari sends as an empty or broken
 * upload; reading it first makes sure the bytes exist — or fails here with a clear message.
 */
export async function readFileForUpload(file: File): Promise<File> {
  let bytes: ArrayBuffer;
  try {
    bytes = await file.arrayBuffer();
  } catch (err) {
    const reason = err instanceof Error && err.message ? ` (${err.message})` : '';
    throw new Error(`"${file.name}" could not be read on this device${reason}. Please try again, or save it to Files first and upload it from there.`);
  }
  if (bytes.byteLength === 0) {
    throw new Error(`"${file.name}" is empty (0 bytes) and was not uploaded.`);
  }
  return new File([bytes], uploadFileName(file.name), {
    type: file.type || 'application/octet-stream',
    lastModified: file.lastModified,
  });
}
